import { z } from 'zod'

/**
 * The canonical Firebase project binding (SCH-2102 U1a; KTD1, KTD3, KTD7).
 *
 * ONE RECORD, DELIBERATELY: the plan's rotation, offboarding and fleet-run
 * records are a `ProvisioningRun` kind and an `OperationsEvent` row
 * respectively, so their vocabularies land with the units that build them
 * (U6, U7, U8) rather than ahead of them.
 *
 * Every vocabulary column on these tables is a string in the database, not a
 * MySQL enum, for the reason {@link ./mobile-app-credential-pointer.ts} gives:
 * each new enum value would cost a hand-performed PlanetScale promotion, and
 * every one of these is expected to grow as U4-U8 land. THIS FILE is where the
 * closed sets live, so a value is validated here rather than by MySQL.
 *
 * Plan: docs/plans/2026-09-21-1241-feat-google-cloud-firebase-fleet-architecture-plan.md
 */

/**
 * The environments a Firebase project can serve (KTD1).
 *
 * Every app has a `production` binding. `development` and `preview` exist only
 * for a designated fixture label; an ordinary school's `.dev` / `.preview`
 * builds get no Firebase project at all (R4, AE4).
 */
export const FIREBASE_ENVIRONMENTS = ['production', 'development', 'preview'] as const
export const FirebaseEnvironmentSchema = z.enum(FIREBASE_ENVIRONMENTS)
export type FirebaseEnvironment = z.infer<typeof FirebaseEnvironmentSchema>

/** How a binding came to exist. `legacy_backfill` is the U1a migration from the flat `MobileApp` columns. */
export const FIREBASE_BINDING_SOURCES = ['legacy_backfill', 'inventory', 'provisioning', 'operator'] as const
export const FirebaseBindingSourceSchema = z.enum(FIREBASE_BINDING_SOURCES)
export type FirebaseBindingSource = z.infer<typeof FirebaseBindingSourceSchema>

/**
 * The project-binding lifecycle (the plan's state diagram).
 *
 * `pending_inventory` is the floor: a binding whose provider identity has not
 * been classified by an approved inventory yet. The backfill writes it for
 * every legacy row, including rows that already carry a project ID, because a
 * recorded ID is not proof the project matches the shipped app.
 *
 * `deletion_eligible` is EVIDENCE ONLY. No transition out of it exists; project
 * deletion needs a separate retention and destructive-approval design.
 */
export const FIREBASE_BINDING_LIFECYCLE_STATES = [
  'pending_inventory',
  'conflict',
  'approved_adopt',
  'approved_move_then_adopt',
  'approved_create',
  'migration',
  'provisioning',
  'blocked',
  'active',
  'rotation_pending',
  'quarantine_pending',
  'quarantined',
  'retained',
  'deletion_eligible'
] as const
export const FirebaseBindingLifecycleStateSchema = z.enum(FIREBASE_BINDING_LIFECYCLE_STATES)
export type FirebaseBindingLifecycleState = z.infer<typeof FirebaseBindingLifecycleStateSchema>

/**
 * An inventory run's classification of one app (KTD3). One exact candidate is
 * `adopt` (or `move_then_adopt` when it sits outside the organization); zero
 * candidates under complete coverage is `create`; anything unreadable,
 * duplicated, split or contradictory is `conflict`.
 */
export const FIREBASE_ALLOCATION_DECISIONS = ['adopt', 'move_then_adopt', 'create', 'conflict'] as const
export const FirebaseAllocationDecisionSchema = z.enum(FIREBASE_ALLOCATION_DECISIONS)
export type FirebaseAllocationDecision = z.infer<typeof FirebaseAllocationDecisionSchema>

/**
 * The outcome of the LAST attempt to observe a binding against its providers.
 *
 * `unreadable` is never `absent`: a `403` or `AUTH_FAILED` leaves the prior
 * facts in place and stops create/adopt decisions (Error semantics table).
 */
export const FIREBASE_BINDING_CHECK_STATUSES = [
  'never_checked',
  'observed',
  'stale_check',
  'unreadable',
  'conflict'
] as const
export const FirebaseBindingCheckStatusSchema = z.enum(FIREBASE_BINDING_CHECK_STATUSES)
export type FirebaseBindingCheckStatus = z.infer<typeof FirebaseBindingCheckStatusSchema>

/** One app's Firebase mapping in one environment, as a client sees it. Public identifiers only. */
export const MobileAppFirebaseProjectSchema = z.object({
  publicId: z.string(),
  /** `MobileApp.publicId`. */
  mobileAppPublicId: z.string(),
  environment: FirebaseEnvironmentSchema,
  googleProjectId: z.string().nullable(),
  /** Also the FCM Sender ID. */
  googleProjectNumber: z.string().nullable(),
  targetParent: z.string().nullable(),
  firebaseAndroidAppId: z.string().nullable(),
  firebaseIosAppId: z.string().nullable(),
  oneSignalAppId: z.string().nullable(),
  source: FirebaseBindingSourceSchema,
  lifecycleState: FirebaseBindingLifecycleStateSchema,
  allocationDecision: FirebaseAllocationDecisionSchema.nullable(),
  /**
   * KTD2's deterministic project-ID candidate index, persisted BEFORE a create
   * so a retry targets the same ID. Null until an allocation picks one.
   */
  allocationCandidateIndex: z.number().int().nonnegative().nullable(),
  /** `OperationsEvent.publicId` for the approved inventory evidence, once U4 records one. */
  allocationEvidencePublicId: z.string().nullable(),
  checkStatus: FirebaseBindingCheckStatusSchema,
  checkError: z.string().nullable(),
  /** ISO-8601. Every attempt. */
  lastCheckedAt: z.string().nullable(),
  /** ISO-8601. Only a successful observation. */
  observedAt: z.string().nullable()
})
export type MobileAppFirebaseProject = z.infer<typeof MobileAppFirebaseProjectSchema>
